import { toast } from 'react-hot-toast';

/**
 * Enhanced error handling utility for the super admin portal
 * Provides user-friendly error messages and automatic focus/scroll behavior
 */

// Error message mappings for common API errors
const ERROR_MESSAGES = {
  // Network errors
  'Network Error': 'Unable to connect to the server. Please check your internet connection.',
  'timeout': 'Request timed out. Please try again.',
  
  // Authentication errors
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  
  // Validation errors
  400: 'Invalid data provided. Please check your input.',
  422: 'Validation failed. Please correct the highlighted fields.',
  
  // Server errors
  500: 'Server error occurred. Please try again later.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service temporarily unavailable. Please try again.',
  
  // Question-specific errors
  'QUESTION_NOT_FOUND': 'Question not found. It may have been deleted.',
  'DUPLICATE_QUESTION': 'A question with similar content already exists.',
  'INVALID_OPTIONS': 'All answer options must be provided and at least one must be marked as correct.',
  'MISSING_REQUIRED_FIELDS': 'Please fill in all required fields.',
  
  // Tenant-specific errors
  'TENANT_CODE_EXISTS': 'This tenant code is already in use. Please choose a different one.',
  'TENANT_NOT_FOUND': 'Tenant not found. It may have been deleted.',
  
  // Kit-specific errors
  'KIT_NOT_FOUND': 'Question kit not found. Please select a valid kit.',
  'KIT_ACCESS_DENIED': 'You do not have access to this question kit.',
  
  // File upload errors
  'FILE_TOO_LARGE': 'File size exceeds the maximum limit of 10MB.',
  'INVALID_FILE_FORMAT': 'Invalid file format. Please upload an Excel file.',
  'EMPTY_FILE': 'The uploaded file is empty.',
  'INVALID_EXCEL_STRUCTURE': 'Excel file structure is invalid. Please check the required columns.',
};

// Prevent accidental duplicate toasts fired by both parent and child handlers.
const TOAST_DEDUPE_WINDOW_MS = 1200;
const recentToasts = new Map();

const shouldSkipDuplicateToast = (type, rawMessage) => {
  const message = String(rawMessage || '').trim();
  if (!message) return false;

  const key = `${type}:${message}`;
  const now = Date.now();
  const lastShownAt = recentToasts.get(key);

  if (lastShownAt && now - lastShownAt < TOAST_DEDUPE_WINDOW_MS) {
    return true;
  }

  recentToasts.set(key, now);
  return false;
};

/**
 * Extract user-friendly error message from API error
 */
export const getErrorMessage = (error) => {
  // Handle network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return ERROR_MESSAGES.timeout;
    }
    return ERROR_MESSAGES['Network Error'];
  }

  const { status, data } = error.response;
  
  // Check for specific error codes in response data
  if (data?.errorCode && ERROR_MESSAGES[data.errorCode]) {
    return ERROR_MESSAGES[data.errorCode];
  }
  
  // Check for custom error messages from backend
  if (data?.message) {
    return data.message;
  }
  
  if (data?.error) {
    return data.error;
  }
  
  // Fallback to status-based messages
  if (ERROR_MESSAGES[status]) {
    return ERROR_MESSAGES[status];
  }
  
  return `An error occurred (${status}). Please try again.`;
};

/**
 * Extract validation errors from API response
 */
export const getValidationErrors = (error) => {
  if (!error.response?.data) return {};
  
  const { data } = error.response;
  
  // Handle Spring Boot validation errors
  if (data.fieldErrors) {
    const errors = {};
    data.fieldErrors.forEach(fieldError => {
      errors[fieldError.field] = fieldError.message;
    });
    return errors;
  }
  
  // Handle custom validation errors
  if (data.validationErrors) {
    return data.validationErrors;
  }
  
  // Handle single field error
  if (data.field && data.message) {
    return { [data.field]: data.message };
  }
  
  return {};
};

/**
 * Show error toast with user-friendly message
 */
export const showErrorToast = (error, customMessage = null) => {
  const message = customMessage || getErrorMessage(error);
  if (shouldSkipDuplicateToast('error', message)) return;

  toast.error(message, {
    id: `error:${message}`,
    duration: 5000,
    position: 'top-right',
    style: {
      background: '#FEF2F2',
      border: '1px solid #FECACA',
      color: '#B91C1C',
    },
  });
};

/**
 * Show success toast
 */
export const showSuccessToast = (message) => {
  if (shouldSkipDuplicateToast('success', message)) return;

  toast.success(message, {
    id: `success:${message}`,
    duration: 3000,
    position: 'top-right',
    style: {
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
      color: '#166534',
    },
  });
};

/**
 * Show warning toast
 */
export const showWarningToast = (message) => {
  if (shouldSkipDuplicateToast('warning', message)) return;

  toast(message, {
    id: `warning:${message}`,
    duration: 4000,
    position: 'top-right',
    icon: '⚠️',
    style: {
      background: '#FFFBEB',
      border: '1px solid #FED7AA',
      color: '#92400E',
    },
  });
};

/**
 * Focus and scroll to the first error field in a form
 */
export const focusFirstError = (errors, formRef = null) => {
  const errorFields = Object.keys(errors);
  if (errorFields.length === 0) return;
  
  const firstErrorField = errorFields[0];
  
  // Try to find the field by name, id, or data-field attribute
  const selectors = [
    `[name="${firstErrorField}"]`,
    `#${firstErrorField}`,
    `[data-field="${firstErrorField}"]`,
    `.field-${firstErrorField} input`,
    `.field-${firstErrorField} select`,
    `.field-${firstErrorField} textarea`,
  ];
  
  const container = formRef?.current || document;
  
  for (const selector of selectors) {
    const element = container.querySelector(selector);
    if (element) {
      // Scroll to element with some offset
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      // Focus the element after a short delay
      setTimeout(() => {
        element.focus();
      }, 300);
      
      break;
    }
  }
};

/**
 * Scroll to error message container
 */
export const scrollToErrorMessage = (errorContainerRef) => {
  if (errorContainerRef?.current) {
    errorContainerRef.current.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });
  }
};

/**
 * Handle form submission errors with comprehensive error display
 */
export const handleFormError = (error, setErrors, formRef = null, errorContainerRef = null) => {
  const validationErrors = getValidationErrors(error);
  
  if (Object.keys(validationErrors).length > 0) {
    // Set field-specific errors
    setErrors(validationErrors);
    
    // Focus first error field
    setTimeout(() => {
      focusFirstError(validationErrors, formRef);
    }, 100);
  } else {
    // Set general error
    const generalError = getErrorMessage(error);
    setErrors({ general: generalError });
    
    // Scroll to error message
    if (errorContainerRef) {
      setTimeout(() => {
        scrollToErrorMessage(errorContainerRef);
      }, 100);
    }
  }
  
  // Also show toast for immediate feedback
  showErrorToast(error);
};

/**
 * Clear all errors
 */
export const clearErrors = (setErrors) => {
  setErrors({});
};

/**
 * Validate required fields
 */
export const validateRequiredFields = (data, requiredFields) => {
  const errors = {};
  
  requiredFields.forEach(field => {
    const value = data[field];
    if (!value || (typeof value === 'string' && !value.trim())) {
      errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }
  });
  
  return errors;
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate tenant code format
 */
export const validateTenantCode = (code) => {
  const codeRegex = /^[A-Z0-9_]+$/;
  return codeRegex.test(code);
};
